import { createContext } from "@utils/CreateContext";
import { ReactNode, useEffect, useState } from "react";
import { getRandomString, hashCode, saveToLS, getFromLS } from "@utils";
import {
  LS_AUTH,
  LINE_AUTHORIZE_URL,
  LINE_AUTHORIZE_CLIENT_ID,
  LINE_AUTHORIZE_REDIRECT_URL,
  LINE_AUTHORIZE_SCOPE,
} from "@configs";

export type AuthorizeStateType = {
  loginMethod?: string;
  codeVerifier?: string;
  state?: string;
  authorizeCode?: string;
};

type AuthContext = {
  init: boolean;
  authorizeState?: AuthorizeStateType;
  onRequestLogin: () => void;
  onChangeLoginMethod: (method?: string) => void;
};

type AuthProviderProps = {
  children: ReactNode;
};

const [Provider, useContext] = createContext<AuthContext>();

const AuthProvider = (props: AuthProviderProps) => {
  const { children } = props;

  const [authorizeState, setAuthorizeState] = useState<AuthorizeStateType>();
  const [init, setInit] = useState<boolean>(false);

  useEffect(() => {
    const dataFromLS: AuthorizeStateType = getFromLS(LS_AUTH);
    setAuthorizeState(dataFromLS);
    setInit(true);
  }, []);

  const onRequestLogin = async () => {
    const randomState = getRandomString(30);
    const randomCodeVerifier = getRandomString(50);

    const dataForSaveToLS: AuthorizeStateType = {
      state: randomState,
      codeVerifier: randomCodeVerifier,
    };

    await saveToLS(LS_AUTH, dataForSaveToLS);

    await hashCode(randomCodeVerifier)
      .then((hash) => {
        let authUrl = "";
        authUrl = `${LINE_AUTHORIZE_URL}/authorize?response_type=code&client_id=${LINE_AUTHORIZE_CLIENT_ID}&redirect_uri=${LINE_AUTHORIZE_REDIRECT_URL}&scope=${LINE_AUTHORIZE_SCOPE}&state=${randomState}&initial_amr_display=lineqr&switch_amr=true&bot_prompt=aggressive&prompt=consent&code_challenge=${hash}&code_challenge_method=S256`;
        window.location.replace(authUrl);
      })
      .catch((error) => {
        console.error("Error:", error);
        // handle error
      });

    return;
  };

  const onChangeLoginMethod = (method?: string) => {
    setAuthorizeState((d) => {
      const data = {
        ...d,
        loginMethod: method,
      };

      saveToLS(LS_AUTH, data);

      return data;
    });
  };

  const authValue = {
    init,
    authorizeState,
    onRequestLogin,
    onChangeLoginMethod,
  };

  return <Provider value={authValue}>{children}</Provider>;
};

export { AuthProvider, useContext as useAuthContext };
